/**
 * Owns Loomua's browser Supabase session, email confirmation and password
 * recovery handoffs, closed social-Provider exposure, and safe Auth return
 * state. It accepts only explicitly public configuration and lets supabase-js
 * persist, refresh, and restore user sessions.
 */
import { createClient, type AuthChangeEvent } from "@supabase/supabase-js";

type SessionDatabase = {
  public: {
    Tables: Record<
      string,
      {
        Row: Record<string, unknown>;
        Insert: Record<string, unknown>;
        Update: Record<string, unknown>;
        Relationships: [];
      }
    >;
    Views: Record<string, never>;
    Functions: Record<string, never>;
    Enums: Record<string, never>;
    CompositeTypes: Record<string, never>;
  };
};

export type PublicSupabaseConfig = {
  url: string;
  publishableKey: string;
};

type PublicEnvironment = {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
  VITE_SOCIAL_AUTH_PROVIDERS?: string;
};

export const socialAuthProviders = ["google", "apple", "discord"] as const;
export type SocialAuthProvider = (typeof socialAuthProviders)[number];
const socialAuthReturnPathKey = "loomua.auth.returnTo";
const browserSessionReadTimeoutMs = 4000;

export function readSocialAuthProviders(
  environment: Pick<PublicEnvironment, "VITE_SOCIAL_AUTH_PROVIDERS">
): SocialAuthProvider[] {
  const configured = new Set(
    environment.VITE_SOCIAL_AUTH_PROVIDERS?.split(",")
      .map((provider) => provider.trim().toLowerCase())
      .filter(Boolean) ?? []
  );
  return socialAuthProviders.filter((provider) => configured.has(provider));
}

export function readPublicSupabaseConfig(
  environment: PublicEnvironment
): PublicSupabaseConfig {
  const url = environment.VITE_SUPABASE_URL?.trim();
  const publishableKey = environment.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

  if (!url || !publishableKey) {
    throw new Error("Missing public Supabase configuration");
  }

  return { url, publishableKey };
}

export function createBrowserSupabaseClient(config: PublicSupabaseConfig) {
  return createClient<SessionDatabase>(config.url, config.publishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true
    }
  });
}

let configuredBrowserClient:
  ReturnType<typeof createBrowserSupabaseClient> | undefined;

/**
 * Returns the one browser client that owns Loomua's persisted auth session.
 * Multiple clients must not compete for the same Supabase storage key.
 */
export function getConfiguredBrowserClient() {
  if (configuredBrowserClient) return configuredBrowserClient;

  const environment = import.meta.env as unknown as PublicEnvironment;
  configuredBrowserClient = createBrowserSupabaseClient(
    readPublicSupabaseConfig({
      VITE_SUPABASE_URL: environment.VITE_SUPABASE_URL,
      VITE_SUPABASE_PUBLISHABLE_KEY: environment.VITE_SUPABASE_PUBLISHABLE_KEY
    })
  );
  return configuredBrowserClient;
}

type BrowserAuthSessionResult = Awaited<
  ReturnType<
    ReturnType<typeof getConfiguredBrowserClient>["auth"]["getSession"]
  >
>;

async function readBrowserAuthSession(): Promise<BrowserAuthSessionResult> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<BrowserAuthSessionResult>((resolve) => {
    timeoutId = globalThis.setTimeout(() => {
      resolve({
        data: { session: null },
        error: null
      });
    }, browserSessionReadTimeoutMs);
  });

  try {
    return await Promise.race([
      getConfiguredBrowserClient().auth.getSession(),
      timeout
    ]);
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

export async function getBrowserAccessToken(): Promise<string | null> {
  try {
    const { data, error } = await readBrowserAuthSession();
    return error ? null : (data.session?.access_token ?? null);
  } catch {
    return null;
  }
}

export type BrowserSession = {
  accessToken: string;
  userId: string;
};

export type PasswordCredentials = {
  email: string;
  password: string;
};

export type PasswordSignUpResult =
  { kind: "signed-in"; session: BrowserSession } | { kind: "confirmation" };

export type PasswordRecoveryState = "ready" | "error";
export type EmailConfirmationState = "pending" | "error";

export function safeAuthReturnPath(value: string | null | undefined): string {
  const candidate = value?.trim();
  if (!candidate?.startsWith("/") || candidate.startsWith("//")) return "/";

  const referenceOrigin = "https://loomua.invalid";
  try {
    const resolved = new URL(candidate, referenceOrigin);
    return resolved.origin === referenceOrigin
      ? `${resolved.pathname}${resolved.search}${resolved.hash}`
      : "/";
  } catch {
    return "/";
  }
}

export function createSocialAuthRequest(
  provider: SocialAuthProvider,
  origin: string
) {
  const trustedOrigin = new URL(origin).origin;
  return {
    provider,
    options: {
      redirectTo: new URL("/", trustedOrigin).toString()
    }
  };
}

export function rememberSocialAuthReturnPath(
  storage: Pick<Storage, "setItem">,
  returnTo: string | null | undefined
): void {
  try {
    storage.setItem(socialAuthReturnPathKey, safeAuthReturnPath(returnTo));
  } catch {
    // OAuth still returns safely to the root when browser storage is unavailable.
  }
}

export function consumeSocialAuthReturnPath(
  storage: Pick<Storage, "getItem" | "removeItem">
): string | undefined {
  try {
    const stored = storage.getItem(socialAuthReturnPathKey);
    if (stored === null) return undefined;
    storage.removeItem(socialAuthReturnPathKey);
    return safeAuthReturnPath(stored);
  } catch {
    return undefined;
  }
}

export function readSocialAuthCallbackError(
  search: string,
  hash = ""
): string | undefined {
  const hasError = [search, hash].some((value) => {
    const parameters = new URLSearchParams(value.replace(/^[?#]/, ""));
    return parameters.has("error") || parameters.has("error_code");
  });
  return hasError
    ? "Social sign-in was cancelled or could not be completed. Try again."
    : undefined;
}

export function createPasswordRecoveryRequest(email: string, origin: string) {
  const trustedOrigin = new URL(origin).origin;
  const callback = new URL("/", trustedOrigin);
  callback.searchParams.set("authCallback", "recovery");
  return {
    email,
    options: {
      redirectTo: callback.toString()
    }
  };
}

export function createEmailConfirmationRequest(email: string, origin: string) {
  const callback = new URL("/", new URL(origin).origin);
  callback.searchParams.set("authCallback", "signup");
  return {
    email,
    options: { emailRedirectTo: callback.toString() },
    type: "signup" as const
  };
}

export function readEmailConfirmationCallback(
  search: string,
  hash = ""
): EmailConfirmationState | undefined {
  const searchParameters = new URLSearchParams(search.replace(/^\?/, ""));
  if (searchParameters.get("authCallback") !== "signup") return undefined;

  const callbackParameters = [search, hash].map(
    (value) => new URLSearchParams(value.replace(/^[?#]/, ""))
  );
  if (
    callbackParameters.some(
      (parameters) => parameters.has("error") || parameters.has("error_code")
    )
  ) {
    return "error";
  }
  return callbackParameters.some(
    (parameters) =>
      (parameters.get("type") === "signup" && parameters.has("access_token")) ||
      parameters.has("code")
  )
    ? "pending"
    : undefined;
}

export function readEmailConfirmationCode(
  search: string,
  hash = ""
): string | undefined {
  if (readEmailConfirmationCallback(search, hash) !== "pending") {
    return undefined;
  }
  for (const value of [search, hash]) {
    const code = new URLSearchParams(value.replace(/^[?#]/, "")).get("code");
    if (code?.trim()) return code.trim();
  }
  return undefined;
}

export function matchesEmailConfirmationSession(
  search: string,
  hash: string,
  accessToken: string
): boolean {
  if (readEmailConfirmationCallback(search, hash) !== "pending") return false;
  return [search, hash].some((value) => {
    const parameters = new URLSearchParams(value.replace(/^[?#]/, ""));
    return (
      parameters.get("type") === "signup" &&
      parameters.get("access_token") === accessToken
    );
  });
}

export async function exchangeEmailConfirmationCodeForSession(
  code: string
): Promise<BrowserSession> {
  try {
    const { data, error } =
      await getConfiguredBrowserClient().auth.exchangeCodeForSession(code);
    if (error) throw error;
    return browserSessionFromSupabaseSession(data.session ?? {});
  } catch {
    throw new Error("Email confirmation could not be completed.");
  }
}

export function readPasswordRecoveryCallback(
  search: string,
  hash = ""
): PasswordRecoveryState | undefined {
  const callbackMarker = new URLSearchParams(search.replace(/^\?/, "")).get(
    "authCallback"
  );
  for (const value of [search, hash]) {
    const parameters = new URLSearchParams(value.replace(/^[?#]/, ""));
    if (parameters.get("type") === "recovery") return "ready";
    if (
      callbackMarker === "recovery" &&
      ["otp_expired", "otp_disabled"].includes(
        parameters.get("error_code") ?? ""
      )
    ) {
      return "error";
    }
  }
  return undefined;
}

/**
 * Keeps email confirmation on the Loomua origin that initiated signup. The
 * root query marker distinguishes signup failures without carrying authority.
 */
export function createPasswordSignUpRequest(
  { email, password }: PasswordCredentials,
  origin: string
) {
  return {
    email,
    password,
    options: createEmailConfirmationRequest(email, origin).options
  };
}

function browserSessionFromSupabaseSession(session: {
  access_token?: string;
  user?: { id?: string };
}): BrowserSession {
  const accessToken = session.access_token;
  const userId = session.user?.id;
  if (!accessToken || !userId) {
    throw new Error("Supabase did not return a complete browser session.");
  }
  return { accessToken, userId };
}

export function resolvePasswordSignUpResult(
  session: Parameters<typeof browserSessionFromSupabaseSession>[0] | null
): PasswordSignUpResult {
  if (!session) return { kind: "confirmation" };
  return {
    kind: "signed-in",
    session: browserSessionFromSupabaseSession(session)
  };
}

export async function signInWithPasswordSession({
  email,
  password
}: PasswordCredentials): Promise<BrowserSession> {
  const { data, error } =
    await getConfiguredBrowserClient().auth.signInWithPassword({
      email,
      password
    });
  if (error) throw new Error(error.message);
  return browserSessionFromSupabaseSession(data.session ?? {});
}

export async function signInWithSocialProvider(
  provider: SocialAuthProvider,
  returnTo: string
): Promise<void> {
  rememberSocialAuthReturnPath(window.sessionStorage, returnTo);
  const { error } = await getConfiguredBrowserClient().auth.signInWithOAuth(
    createSocialAuthRequest(provider, window.location.origin)
  );
  if (error) {
    consumeSocialAuthReturnPath(window.sessionStorage);
    throw new Error("Social sign-in is unavailable. Try again.");
  }
}

export async function signUpWithPasswordSession({
  email,
  password
}: PasswordCredentials): Promise<PasswordSignUpResult> {
  const { data, error } = await getConfiguredBrowserClient().auth.signUp(
    createPasswordSignUpRequest({ email, password }, window.location.origin)
  );
  if (error) throw new Error(error.message);
  return resolvePasswordSignUpResult(data.session);
}

export async function requestPasswordRecovery(email: string): Promise<void> {
  const request = createPasswordRecoveryRequest(email, window.location.origin);
  const { error } =
    await getConfiguredBrowserClient().auth.resetPasswordForEmail(
      request.email,
      request.options
    );
  if (error) {
    throw new Error("Password recovery email could not be sent. Try again.");
  }
}

export async function resendEmailConfirmation(email: string): Promise<void> {
  const { error } = await getConfiguredBrowserClient().auth.resend(
    createEmailConfirmationRequest(email, window.location.origin)
  );
  if (error) {
    throw new Error("Confirmation email could not be sent. Try again later.");
  }
}

export function navigateBrowser(path: string): void {
  window.location.assign(path);
}

export async function updateRecoveredPassword(password: string): Promise<void> {
  const { error } = await getConfiguredBrowserClient().auth.updateUser({
    password
  });
  if (error) {
    throw new Error("Password could not be updated. Request a new reset link.");
  }
}

export async function getBrowserSession(): Promise<BrowserSession | null> {
  try {
    const { data, error } = await readBrowserAuthSession();
    const session = data.session;
    return error || !session
      ? null
      : { accessToken: session.access_token, userId: session.user.id };
  } catch {
    return null;
  }
}

export function subscribeToBrowserSession(
  onSession: (
    session: BrowserSession | null,
    event: AuthChangeEvent | undefined
  ) => void
): () => void {
  try {
    const client = getConfiguredBrowserClient();
    const { data } = client.auth.onAuthStateChange((event, session) => {
      onSession(
        session
          ? { accessToken: session.access_token, userId: session.user.id }
          : null,
        event
      );
    });
    return () => data.subscription.unsubscribe();
  } catch {
    onSession(null, undefined);
    return () => undefined;
  }
}

export async function signOutBrowserSession(): Promise<void> {
  try {
    await getConfiguredBrowserClient().auth.signOut();
  } catch {
    // An absent public configuration already represents an anonymous browser.
  }
}
